import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDebounce } from "../core/hooks/useDebounce";
import { ContainerState, EntityLookupElement, PresenterState, useContainer, usePresenter } from "./types"
import { useApi } from "../core/hooks/useApi";
import { api } from "../api/api";
import { DataState, useData } from "../core/hooks/useData";
import { Table } from "../core/ui/Table";
import { CustomCol, TitleCol } from "../layout/TableCells";
import { Address, Company, CompanyName, EntityData, Individual, Name, TrackedItem, Trust, entity } from "../types";
import { newID } from "../core/utils/unique";
import { useGlobalHandler } from "../core/hooks/useGlobalHandler";
import { closestKey } from "../core/utils/dom";
import { last } from "../core/utils/array";
import { Icon } from "../core/ui/icons";
import { setObjectValue } from "../core/utils/objectPath";
import { AddressCol } from "../pages/entity/Address";
import { formatACN, formatter } from "../formatters/format";

export function EntityLookup(props: {
	def: EntityLookupElement,
}) {
	const presenter = usePresenter();
	const container = useContainer();
	const data = useData();

	const id = useRef("");
	if (!id.current) id.current = newID();

	const [search, setSearch] = useDebounce(750, {
		qry: "",
	});

	const [val, setVal] = useState("");

	const [showingResults, setShowingResults] = useState(false);
	const [haveSearched, setHaveSearched] = useState(false);

	const [results, setResults] = useState<entity[] | undefined>();

	let resultsToUse = results;

	useEffect(() => {
		if (!search.qry) return;

		let cancelled = false;

		api.entity_search({
			types: props.def.requiredTypes,
			qry: search.qry,
		}).then((response) => {
			if (cancelled) return;
			if (response && response.length > 0) setShowingResults(true);
			setResults(response);

		}).catch(() => {
			// ignore errors from cancelled/stale requests
		});

		return () => {
			cancelled = true;
		};
	}, [search]);

	useGlobalHandler("mousedown", useCallback((e: any) => {
		const key = closestKey(e.target, ".entity-search");
		if (!key) {
			setShowingResults(false);
		}
	}, []));

	useLayoutEffect(() => {
		if (!haveSearched && search.qry) {
			const el = document.getElementById(id.current) as any;
			el.focus();
		}
	}, [haveSearched]);

	return (
		<div style={{position:"relative"}} className="entity-search" data-key={id.current}>
			<div className="flex horiz align-center gap-5 search-box">
				<Icon type="search" size="small" />

				{
					!haveSearched &&
					<input
						id={id.current}
						placeholder="Search"
						className="form-control font-small"
						type="text"
						style={{width: "100%"}}
						onChange={(e: any) => {
							const val = e.target.value;

							setVal(val);
							setSearch({
								qry: val,
							});
						}}
						onFocus={() => {
							if (!showingResults) setShowingResults(true);
						}}
						value={val}
					/>
				}

				{
					haveSearched &&
					<button
						className="flat"
						onClick={() => {
							setHaveSearched(false);
						}}
					>Search Again</button>
				}


			</div>

			{
				resultsToUse && resultsToUse.length > 0 &&
				showingResults &&
				<Table
					style={{position: "absolute", minWidth: "350px", left: "40px", boxShadow: "0 15px 20px rgba(0,0,0,0.3)"}}
					className={"z-top bg row-vert-top"}
					cols={[
						//TitleCol("Matches"),
						CustomCol({
							title: () => "Matches",
							value: (r, c) => {
								let extra;

								const e = r.data as EntityData;
								if (e.type === "individual") {
									const i = e as Individual;
									if (i.dob) {
										extra = <div>{formatter(i.dob, "date/short")}</div>
									}
								} else if (e.type === "company") {
									const c = e as Company;
									if (c.acn) {
										extra = <div>{formatter(c.acn, "acn")}</div>;
									}
								}

								return (
									<>
										<b>{r.title}</b>
										{extra}
									</>
								)
							},
						}),
						AddressCol({
							className: "font-small"
						}),
					]}
					items={resultsToUse}
					interactive={true}
					onClick={(e, r) => {
						copyFromEntity(r, presenter, container, props.def);
						setShowingResults(false);
						setHaveSearched(true);
						setResults(undefined)
					}}
				/>
			}
		</div>
	)
}



export function EntitySearch(props: {
	options: any,
}) {
	return (
		<EntityLookup def={props.options} />
	)
}

function copyFromEntity(ent: entity, presenter: PresenterState, container: ContainerState, def?: EntityLookupElement) {

	function copyCompanyName(prop: string, history: CompanyName[]) {
		let item = last(history) as any;
		if (!item) return;

		item = {
			name: "",
			...item,
		};

		presenter.setChildValue(prop, item.name, container);
	}


	function copyIndividualName(prop: string, history: Name[]) {
		let item = last(history) as any;
		if (!item) return;

		item = {
			display: "",
			title: "",
			given1: "",
			given2: "",
			given3: "",
			family: "",
			...item,
		};

		for(let m in item) {
			if (m === "ceased" || m === "effective") continue;
			const v = item[m];

			presenter.setChildValue(prop ? (prop + "." + m) : m, v, container);
		}
	}

	function copyAddress(prop: string, addresses: Address[]) {
		debugger;

		let addr = last(addresses) as any;
		if (!addr) return;

		// want to clear out values not present in the entity as well
		addr = {
			careOf: "",
			line2: "",
			street: "",
			locality: "",
			state: "",
			postCode: "",
			country: "",
			...addr,
		};

		for(var m in addr) {
			presenter.setChildValue(`${prop}.${m}`, addr[m], container);
		}

		presenter.setChildValue(`address.search_manual`, true, container);
	}

	function copyValue(prop: string, value: any) {
		presenter.setChildValue(prop, value, container);
	}

	// depending on what the entity type is it will need to copy different things
	if (ent.data.type === "individual") {
		const individual = ent.data as Individual;
		debugger;
		copyIndividualName("", individual.names);
		
		copyAddress((def && def.addressProp) ? def.addressProp : "address", individual.addresses);

		copyValue("dob", individual.dob);
		if (individual.pob) {
			copyValue("pob.suburb", individual.pob.suburb);
			copyValue("pob.locality", individual.pob.locality);
			copyValue("pob.state", individual.pob.state);
			copyValue("pob.country", individual.pob.country);
			copyValue("pob.postcode", individual.pob.postcode);	
		}
	
	} else if (ent.data.type === "company") {
		const company = ent.data as Company;
		copyCompanyName((def && def.companyProp) ? def.companyProp : "name", company.names);
		copyValue((def && def.acnProp) ? def.acnProp : "acn", company.acn);
		copyAddress((def && def.addressProp) ? def.addressProp : "address", company.regOffs);

	} else if (ent.data.type === "trust") {
		const trust = ent.data as Trust;
		copyCompanyName("name", trust.names);
		copyAddress("address", trust.addresses);

	}


}